import i18n from '@/lib/i18n';

import {
  klassesQueryKeys,
  toolsQueryKeys,
  unitsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { ListUsersFilters } from '@/data/services/userServices';
import User from '@/models/User';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { MultipleFilterProps } from '@/models/FilterConfig';
import { ListUnitsFilters } from '@/data/services/unitServices';
import Unit from '@/models/_deprecated/Unit';
import Tool from '@/models/Tool';
import { ListToolsFilters } from '@/data/services/toolServices';
import { ListKlassesFilters } from '@/data/services/klassServices';
import Klass from '@/models/Klass';

type BaseCreateServiceFilter = {
  name?: string;
  label?: string;
  hide?: boolean;
};

type CreateServiceFilter<Filter> = {
  filters?: Filter;
  withSearch?: boolean;
};

type CreateMultipleServiceFilter<T, Filter> = CreateServiceFilter<Filter> &
  BaseCreateServiceFilter & {
    values?: T[];
  };

export function createMultipleUserFilter(
  props: CreateMultipleServiceFilter<number, ListUsersFilters> = {},
): MultipleFilterProps<User, number, ListUsersFilters> {
  const {
    name = 'userId',
    label = i18n.t('common.user'),
    values = [],
    filters,
    hide,
  } = props;

  return {
    name,
    label,
    type: 'multiple',
    mode: 'dynamic',
    service: usersQueryKeys.list,
    filters,
    makeOptions: options =>
      options.map(option => ({
        label: handleUserFullName(option),
        value: option.id,
      })),
    values,
    hide,
  };
}

export function createMultipleUnitFilter(
  props: CreateMultipleServiceFilter<number, ListUnitsFilters> = {},
): MultipleFilterProps<Unit, number, ListUnitsFilters> {
  const {
    name = 'unitId',
    label = i18n.t('common.unit'),
    filters,
    hide,
    withSearch = true,
  } = props;

  return {
    name,
    label,
    type: 'multiple',
    mode: 'dynamic',
    service: unitsQueryKeys.list,
    filters,
    withSearch,
    makeOptions: options =>
      options.map(option => ({
        label: option.name,
        value: option.id,
      })),
    hide,
  };
}

export function createMultipleToolFilter(
  props: CreateMultipleServiceFilter<number, ListToolsFilters> = {},
): MultipleFilterProps<Tool, number, ListToolsFilters> {
  const {
    name = 'toolsId',
    label = i18n.t('common.tool'),
    filters,
    hide,
  } = props;

  return {
    name,
    label,
    type: 'multiple',
    mode: 'dynamic',
    service: toolsQueryKeys.list,
    filters,
    makeOptions: options =>
      options.map(option => ({
        label: option.name,
        value: option.id,
      })),
    hide,
  };
}

export function createMultipleKlassFilter(
  props: CreateMultipleServiceFilter<number, ListKlassesFilters> = {},
): MultipleFilterProps<Klass, number, ListKlassesFilters> {
  const {
    name = 'klassId',
    label = i18n.t('common.klass'),
    filters,
    hide,
    withSearch = true,
  } = props;

  return {
    name,
    label,
    type: 'multiple',
    mode: 'dynamic',
    service: klassesQueryKeys.list,
    filters,
    withSearch,
    makeOptions: options =>
      options.map(option => ({
        label: option.name,
        value: option.id,
      })),
    hide,
  };
}

export function createMultipleTeacherFilter(
  props: CreateMultipleServiceFilter<number, ListUsersFilters> = {},
): MultipleFilterProps<User, number, ListUsersFilters> {
  const {
    name = 'teacherId',
    label = i18n.t('common.teacher'),
    filters,
    hide,
    withSearch = true,
  } = props;

  return {
    name,
    label,
    type: 'multiple',
    mode: 'dynamic',
    service: usersQueryKeys.list,
    filters,
    withSearch,
    makeOptions: options =>
      options.map(option => ({
        label: handleUserFullName(option),
        value: option.id,
      })),
    hide,
  };
}
